import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Glow } from '@/components/generals/glow'
import { Reveal } from '@/components/generals/reveal'
import { Section } from '@/components/generals/section'
import { SectionHeading } from '@/components/generals/section-heading'
import { serviceCards } from '@/constants/content'

const hrefs = ['/services#meta-ads', '/services#google-ads', '/services#tiktok-ads']

export const Services: FC = (): ReactNode => {
    return (
        <Section id='services'>
            <SectionHeading
                eyebrow='Services'
                segments={[
                    { text: 'How can we help' },
                    { text: 'you?', serif: true }
                ]}
                subtitle='From Google ads, to Meta ads, to TikTok ads, and even content creation, we’ve got you covered on everything front.'
            />

            <Reveal
                y={28}
                blur={10}
                delay={0.1}
                stagger={0.12}
                className='mt-14 grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:mt-20'
            >
                {serviceCards.map((service, index) => (
                    <Link
                        key={service.title}
                        href={hrefs[index]}
                        className={cn(
                            'group relative flex min-h-[260px] flex-col justify-between overflow-hidden rounded-2xl bg-surface-1 p-7 ring-1 ring-white/8 transition-[transform,box-shadow] duration-500 ease-[cubic-bezier(0.44,0,0.56,1)] hover:-translate-y-1 hover:ring-white/16 sm:min-h-[300px] sm:p-8',
                            index === 2 && 'md:col-span-2'
                        )}
                    >
                        <Glow
                            variant='card'
                            className={cn(
                                'opacity-80 transition-opacity duration-700 ease-[cubic-bezier(0.44,0,0.56,1)] group-hover:opacity-100',
                                index === 1
                                    ? 'top-[-400px] right-[-260px]'
                                    : 'top-[-400px] left-[-260px]'
                            )}
                        />

                        <div className='relative z-1 flex items-start justify-between gap-4'>
                            <h3 className='text-[clamp(1.5rem,2.6vw,2rem)] leading-[1.1] font-medium tracking-[-0.04em] text-white'>
                                {service.title}
                            </h3>

                            <span
                                aria-hidden='true'
                                className='flex size-9 shrink-0 items-center justify-center rounded-full bg-white/8 text-white ring-1 ring-white/10 transition-all duration-500 ease-[cubic-bezier(0.44,0,0.56,1)] group-hover:bg-[#8b5cf6] group-hover:ring-[#8b5cf6]'
                            >
                                <ArrowUpRight className='size-4' />
                            </span>
                        </div>

                        <p
                            className={cn(
                                'relative z-1 mt-6 text-[15px] leading-[1.55] tracking-[-0.01em] text-white/56',
                                index === 2 ? 'max-w-[62ch]' : 'max-w-[40ch]'
                            )}
                        >
                            {service.body}
                        </p>
                    </Link>
                ))}
            </Reveal>
        </Section>
    )
}
